import { useEffect } from "react";
import { motion, useReducedMotion } from "framer-motion";
import { ease } from "./utils";

export default function IntroOverlay({ onComplete }) {
  const reduce = useReducedMotion();

  useEffect(() => {
    const hold = reduce ? 550 : 1350;
    const t = setTimeout(onComplete, hold);
    return () => clearTimeout(t);
  }, [onComplete, reduce]);

  const particles = [
    { x: -120, d: 5.5, delay: 0.1, s: 2 },
    { x: -60, d: 6.5, delay: 0.5, s: 1.5 },
    { x: 70, d: 5, delay: 0.3, s: 2 },
    { x: 130, d: 7, delay: 0.2, s: 1.5 },
    { x: 0, d: 6, delay: 0.7, s: 2.5 },
  ];

  return (
    <motion.div
      data-testid="intro-overlay"
      initial={{ opacity: 1 }}
      exit={{ opacity: 0 }}
      transition={{ duration: 0.6, ease }}
      className="fixed inset-0 z-[100] flex items-center justify-center bg-brand-gray-light"
      style={{ perspective: 1200 }}
    >
      {/* breathing gold glow */}
      <motion.div
        className="pointer-events-none absolute h-[360px] w-[360px] rounded-full bg-brand-gold/25 blur-[110px]"
        initial={{ opacity: 0, scale: 0.7 }}
        animate={reduce ? { opacity: 0.6 } : { opacity: [0, 0.7, 0.5], scale: [0.7, 1.1, 1] }}
        transition={{ duration: 1.4, ease }}
      />

      {/* faint upward gold flow particles */}
      {!reduce &&
        particles.map((p, i) => (
          <motion.span
            key={i}
            className="pointer-events-none absolute bottom-1/2 rounded-full bg-brand-gold"
            style={{ width: p.s, height: p.s * 5, x: p.x }}
            initial={{ opacity: 0, y: 80 }}
            animate={{ opacity: [0, 0.55, 0], y: -120 }}
            transition={{ duration: p.d, delay: p.delay, repeat: Infinity, ease: "easeOut" }}
          />
        ))}

      {/* logo with light sweep + 3D depth */}
      <div className="relative" style={{ transformStyle: "preserve-3d" }}>
        <motion.div
          className="relative"
          initial={{ opacity: 0, scale: 0.82, y: 14, filter: "blur(9px)", rotateX: 8 }}
          animate={
            reduce
              ? { opacity: 1, scale: 1, y: 0, filter: "blur(0px)", rotateX: 0 }
              : { opacity: 1, scale: 1, y: [14, -7, 0], filter: "blur(0px)", rotateX: 0 }
          }
          transition={{
            duration: reduce ? 0.45 : 1,
            ease,
            y: { duration: 1.05, ease: [0.34, 1.3, 0.5, 1] },
          }}
          style={{ filter: "drop-shadow(0 26px 50px rgba(17,17,17,0.18))" }}
        >
          <img
            src="/apex-flow-logo.png"
            alt="Apex Flow AI"
            className="relative z-10 h-40 w-auto object-contain sm:h-52"
            draggable={false}
          />

          {/* gold light sweep */}
          {!reduce && (
            <motion.div
              className="pointer-events-none absolute inset-0 z-20 overflow-hidden"
              initial={{ opacity: 0 }}
              animate={{ opacity: [0, 1, 0] }}
              transition={{ duration: 1.1, delay: 0.45, ease }}
            >
              <motion.div
                className="absolute top-0 h-full w-1/3 -skew-x-12"
                style={{
                  background:
                    "linear-gradient(90deg, transparent, rgba(200,154,61,0.45), rgba(255,255,255,0.25), transparent)",
                  mixBlendMode: "overlay",
                }}
                initial={{ left: "-40%" }}
                animate={{ left: "120%" }}
                transition={{ duration: 1, delay: 0.45, ease }}
              />
            </motion.div>
          )}
        </motion.div>
      </div>
    </motion.div>
  );
}
